import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../../css/CashCollections.css";
import { fetchCashCollectionLogs } from "../../service/api/adminApi";


export default function CashCollectionLogs() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get("user_id");

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        from: 0,
        to: 0,
    });
    const [search, setSearch] = useState("");
    const [perPage, setPerPage] = useState(10);
    const [details, setDetails] = useState(null);

    const loadLogs = async () => {
        try {
            setLoading(true);

            const res = await fetchCashCollectionLogs({
                user_id: userId || undefined,
                search,
                per_page: perPage,
                page,
            });

            const pagination = res?.meta || res || {};
            setLogs(res?.data || []);
            setMeta({
                current_page: pagination.current_page || page,
                last_page: pagination.last_page || 1,
                total: pagination.total || 0,
                from: pagination.from || 0,
                to: pagination.to || 0,
            });
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Failed to load logs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLogs();
    }, [userId, search, perPage, page]);

    const resetFilters = () => {
        setSearch("");
        setPerPage(10);
        setPage(1);
    };

    const exportCsv = () => {
        const header = [
            "Delivery Guy Name",
            "Phone",
            "Amount",
            "Message",
            "Date",
            "Collected By",
        ];

        const body = logs.map((log) => [
            log.name,
            log.phone,
            Number(log.amount || 0).toFixed(2),
            log.message,
            log.created_at,
            log.collected_by,
        ]);

        const csv = [header, ...body].map((row) => row.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `cash-collection-logs-${new Date()
            .toISOString()
            .slice(0, 10)}.csv`;
        a.click();

        URL.revokeObjectURL(url);
    };

    const renderMessage = (message) => {
        const safeMessage = message || "--";
        const match = safeMessage.match(/pay_[A-Za-z0-9]+/);

        if (!match) return safeMessage;
        const paymentId = match[0];

        return (
            <>
                {message.replace(paymentId, "")}
                <a
                    href={`https://dashboard.razorpay.com/app/payments/${paymentId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cash-link"
                >
                    {paymentId}
                </a>
            </>
        );
    };

    return (
        <div className="cash-page">
            <div className="cash-head">
                <div>
                    <h2>Delivery Collection Logs</h2>
                    <p>View delivery cash collection history</p>
                </div>

                <div className="cash-toolbar-actions">
                    <button
                        type="button"
                        className="cash-reset-btn"
                        onClick={() => navigate("/finance/cash-collections")}
                    >
                        Back
                    </button>

                    <button type="button" className="cash-reset-btn" onClick={resetFilters}>
                        Reset All Filters
                    </button>
                </div>
            </div>

            <div className="cash-card">
                <div className="cash-toolbar">
                    <input
                        className="cash-search"
                        type="text"
                        placeholder="Search with anything..."
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />

                    <div className="cash-toolbar-actions">
                        <button type="button" className="cash-export-btn" onClick={exportCsv}>
                            Export as CSV
                        </button>

                        <select
                            className="cash-per-page"
                            value={perPage}
                            onChange={(event) => setPerPage(Number(event.target.value))}
                        >
                            {[10, 25, 50, 100, 200, 500].map((value) => (
                                <option key={value} value={value}>
                                    {value}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="cash-table-wrap">
                    <table className="cash-table">
                        <thead>
                        <tr>
                            <th>Delivery Guy Name</th>
                            <th>Phone</th>
                            <th>Amount</th>
                            <th>Message</th>
                            <th>Date</th>
                            <th>Collected By</th>
                            <th className="cash-center">Action</th>
                        </tr>
                        </thead>

                        <tbody>
                        {logs.map((log) => (
                            <tr key={log.id}>
                                <td>{log.name}</td>
                                <td>{log.phone}</td>
                                <td>₹{Number(log.amount || 0).toFixed(2)}</td>
                                <td>{log.message || "--"}</td>
                                <td>{log.created_at}</td>
                                <td>{log.collected_by || "--"}</td>
                                <td className="cash-action-cell">
                                    <button
                                        type="button"
                                        className="cash-process-btn"
                                        onClick={() => setDetails(log)}
                                    >
                                        Details
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {loading && (
                            <tr>
                                <td colSpan="7" className="cash-empty">
                                    Loading...
                                </td>
                            </tr>
                        )}
                        {!loading && logs.length === 0 && (
                            <tr>
                                <td colSpan="7" className="cash-empty">
                                    No data found
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                <div className="cash-pagination">
  <span className="cash-pagination-summary">
    {meta.total > 0
        ? `Showing ${meta.from || (page - 1) * perPage + 1} to ${
            meta.to || (page - 1) * perPage + logs.length
        } of ${meta.total} entries`
        : "Showing 0 entries"}
  </span>

                    <div className="cash-page-buttons">
                        <button
                            type="button"
                            className="cash-page-btn"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                        >
                            Previous
                        </button>

                        <span className="cash-page-info">
      Page {meta.current_page || page} of {meta.last_page}
    </span>

                        <button
                            type="button"
                            className="cash-page-btn"
                            disabled={loading || page >= meta.last_page}
                            onClick={() => setPage((prev) => Math.min(prev + 1, meta.last_page))}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {details && (
                <div className="cash-modal-overlay" onMouseDown={() => setDetails(null)}>
                    <div className="cash-modal" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="cash-modal-head">
                            <h3>Cash Collection Details</h3>

                            <button type="button" onClick={() => setDetails(null)}>
                                ×
                            </button>
                        </div>

                        <table className="cash-details-table">
                            <tbody>
                            <tr>
                                <td>Name</td>
                                <td>{details.name}</td>
                            </tr>
                            <tr>
                                <td>Email</td>
                                <td>{details.email}</td>
                            </tr>
                            <tr>
                                <td>Phone</td>
                                <td>{details.phone}</td>
                            </tr>
                            <tr>
                                <td>Amount</td>
                                <td>₹{Number(details.amount || 0).toFixed(2)}</td>
                            </tr>
                            <tr>
                                <td>Date</td>
                                <td>{details.created_at}</td>
                            </tr>
                            <tr>
                                <td>Message</td>
                                <td>{renderMessage(details.message)}</td>
                            </tr>
                            <tr>
                                <td>Collected By</td>
                                <td>{details.collected_by}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}